var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import qs from 'qs';
import { add, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Bar, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import { blue, green, orange, purple, red, cyan } from '@mui/material/colors';
var pallets = [
    blue[500],
    green[500],
    orange[500],
    red[500],
    cyan[500],
    purple[500],
];
var TypicaStockItemDelaySummaryChart = function (_a) {
    var title = _a.title, baseUrl = _a.baseUrl, offerList = _a.offerList, summaryData = _a.summaryData;
    var t = useTranslation().t;
    var navigate = useNavigate();
    var _b = React.useState([]), hideKeys = _b[0], setHideKeys = _b[1];
    var translate = React.useCallback(function (key) {
        return t("pages.dashboard.typicaStockItemDelaySummaryPanel.".concat(key));
    }, [t]);
    var data = React.useMemo(function () {
        var defaultData = [
            { name: translate('within1month') },
            { name: translate('within2month') },
            { name: translate('within3month') },
            { name: translate('within4month') },
            { name: translate('within5month') },
            { name: translate('over5month') },
        ];
        var data = summaryData
            .sort(function (a, b) { return a.groupName.localeCompare(b.groupName); })
            .reduce(function (result, current) {
            result[0][current.id] = current.within1month;
            result[1][current.id] = current.within2month;
            result[2][current.id] = current.within3month;
            result[3][current.id] = current.within4month;
            result[4][current.id] = current.within5month;
            result[5][current.id] = current.over5month;
            return result;
        }, defaultData);
        return data;
    }, [summaryData, translate]);
    var maxQuantity = React.useMemo(function () {
        var values = [];
        data.forEach(function (el) {
            Object.keys(el).forEach(function (key) {
                if (key === 'name' || hideKeys.includes(key))
                    return;
                values.push(el[key]);
            });
        });
        if (values.length === 0)
            return 0;
        var max = values.reduce(function (result, current) {
            var v = parseFloat(current);
            return result > v ? result : v;
        }, 0);
        return max + (100 - (max % 100));
    }, [data, hideKeys]);
    var renderTooltip = React.useCallback(function (_a) {
        var label = _a.label, payload = _a.payload;
        if (!label || !payload)
            return null;
        return (React.createElement(Box, { sx: {
                backgroundColor: 'white',
                padding: '4px 8px',
                '&> p': {
                    fontSize: '9px',
                    margin: 0,
                },
            } },
            React.createElement("p", null, label),
            payload.map(function (data, index) {
                return (React.createElement("p", { key: index, style: { color: data.color } },
                    data.name,
                    ": ",
                    data.value));
            })));
        return null;
    }, []);
    var handleClickLegend = React.useCallback(function (data) {
        if (!data)
            return;
        setHideKeys(function (oldKeys) {
            if (oldKeys.includes(data.dataKey)) {
                return oldKeys.filter(function (key) { return key !== data.dataKey; });
            }
            else {
                return __spreadArray(__spreadArray([], oldKeys, true), [data.dataKey], false);
            }
        });
    }, []);
    return (React.createElement(Box, null,
        React.createElement(Typography, { variant: "h6", sx: {
                padding: '5px',
                textAlign: 'center',
            } }, title),
        React.createElement(ResponsiveContainer, { width: "100%", height: 200 },
            React.createElement(ComposedChart, { data: data, margin: {
                    top: 30,
                } },
                React.createElement(XAxis, { dataKey: "name" }),
                React.createElement(YAxis, { domain: [0, maxQuantity], label: {
                        value: translate('unitLabel'),
                        position: 'top',
                        offset: 15,
                    } }),
                React.createElement(Tooltip, { content: renderTooltip }),
                React.createElement(Legend, { onClick: handleClickLegend }),
                summaryData.map(function (el, index) {
                    var fill = pallets[index % pallets.length];
                    if (el.id.endsWith('-all')) {
                        return (React.createElement(Line, { hide: hideKeys.includes(el.id), key: el.id, dataKey: el.id, name: el.groupName, fill: fill }));
                    }
                    return (React.createElement(Bar, { key: el.id, hide: hideKeys.includes(el.id), dataKey: el.id, name: el.groupName, fill: fill, onClick: function (_, index) {
                            var _a;
                            var groupId = (_a = el.id.split('-')[1]) !== null && _a !== void 0 ? _a : '';
                            navigate(buildUrl(baseUrl, groupId, index, offerList));
                        } }));
                })))));
};
export default TypicaStockItemDelaySummaryChart;
var buildUrl = function (baseUrl, officeId, index, offerList) {
    var current = new Date();
    var startedAt = current;
    var endedAt = current;
    if (index === 0) {
        // within1month
        startedAt = add(current, { months: -1 });
        endedAt = null;
    }
    else if (index === 1) {
        // within2month
        startedAt = add(current, { months: -2 });
        endedAt = add(current, { months: -1 });
    }
    else if (index === 2) {
        // within3month
        startedAt = add(current, { months: -3 });
        endedAt = add(current, { months: -2 });
    }
    else if (index === 3) {
        // within4month
        startedAt = add(current, { months: -4 });
        endedAt = add(current, { months: -3 });
    }
    else if (index === 4) {
        // within5month
        startedAt = add(current, { months: -5 });
        endedAt = add(current, { months: -4 });
    }
    else {
        // over5month
        startedAt = null;
        endedAt = add(current, { months: -5 });
    }
    var filters = {
        'warehouse.office.id': [officeId],
        importedAt: {
            startedAt: startedAt && format(startedAt, 'yyyy-MM-dd'),
            endedAt: endedAt && format(endedAt, 'yyyy-MM-dd'),
        },
        remainingQuantity: 'inStock',
        isTypicaSelection: 'checked',
    };
    if (offerList)
        filters['offer.offerList.name'] = offerList.name;
    return "".concat(baseUrl, "?").concat(qs.stringify({ filters: filters }));
};
